import { useCallback } from 'react'
import { usePage } from '@inertiajs/react'
import type { SharedProps } from '@/types'

/**
 * Returns a function that prefixes an app path with the current account slug,
 * e.g. `ap('/surveys')` → `/abc123xyz0/surveys`, matching the Basecamp-style
 * `/:account_id/*` routes.
 *
 * Use ONLY for account-scoped paths (surveys, settings, team, billing,
 * facilitators, survey_templates, api-keys, onboarding). Do NOT prefix
 * top-level paths (`/login`, `/logout`, `/`, marketing, respondent links).
 *
 * Account-scoped pages always carry `current_account`; if it's somehow absent
 * the path is returned unchanged (defensive no-op).
 */
export function useAccountPath(): (path: string) => string {
  const props = usePage().props as unknown as SharedProps
  const slug = props.current_account?.slug

  // Memoized on the slug so callers can safely list `ap` in useCallback/useMemo
  // dependency arrays.
  return useCallback((path: string): string => {
    if (!slug) return path
    const clean = path.startsWith('/') ? path : `/${path}`
    return `/${slug}${clean}`
  }, [slug])
}
